import type { EChartsOption } from 'echarts-for-react';
import { useMemo } from 'react';

import type { CompressorScheduleForecast } from '@repo/sdk/models/components';

import { formatFacilityDate } from '@/helpers/datetime/facility-date';

import {
  CHART_FONT_FAMILY,
  CHART_FORECAST_MINUTES,
  CHART_GRID_BOTTOM,
  CHART_GRID_TOP,
  CHART_LOOKBACK_MINUTES,
} from './compressor-schedule-chart.constants';
import {
  createScheduleRenderItem,
  segmentToTuple,
  transformForecastToSegments,
} from './compressor-schedule-chart.utils';

/**
 * Compose the full ECharts option for the compressor schedule forecast chart.
 *
 * - X-axis: time (pinned from LOOKBACK_MINUTES ago to FORECAST_MINUTES ahead)
 * - Y-axis: category (compressor names, inverted so first is at top)
 * - Series: single custom series using renderItem for bars + hatching
 */
export const useCompressorScheduleChartOptions = (
  forecast: CompressorScheduleForecast | undefined,
  now: number,
  compressorOrder: string[]
): EChartsOption => {
  return useMemo<EChartsOption>(() => {
    if (!forecast || forecast.steps.length === 0) {
      return { series: [] };
    }

    const renderItem = createScheduleRenderItem();

    const xMin = now - CHART_LOOKBACK_MINUTES * 60 * 1000;
    const xMax = now + CHART_FORECAST_MINUTES * 60 * 1000;

    const segments = transformForecastToSegments(forecast, compressorOrder);
    const data = segments
      .filter((s) => s.endTime > xMin && s.startTime < xMax)
      .map((s) => ({
        ...s,
        startTime: Math.max(s.startTime, xMin),
        endTime: Math.min(s.endTime, xMax),
      }))
      .map(segmentToTuple);

    return {
      animation: false,
      grid: {
        left: 0,
        right: 16,
        top: CHART_GRID_TOP,
        bottom: CHART_GRID_BOTTOM,
      },
      xAxis: {
        type: 'time',
        min: xMin,
        max: xMax,
        axisLabel: {
          color: '#a1a1aa',
          fontSize: 11,
          fontFamily: CHART_FONT_FAMILY,
          formatter: (value: number) => {
            return formatFacilityDate({
              date: value,
              options: {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              },
              withTimeZone: false,
            });
          },
        },
        axisLine: { lineStyle: { color: '#3f3f46' } },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'category',
        data: compressorOrder,
        inverse: true,
        axisLabel: { show: false },
        axisLine: { show: true, lineStyle: { color: '#3f3f46' } },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { color: '#27272a', type: 'dashed' },
        },
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: '#18181b',
        borderColor: '#3f3f46',
        textStyle: { color: '#e4e4e7', fontSize: 12 },
        formatter: (params: unknown) => {
          const p = params as { value: unknown[] };
          if (!p?.value) return '';
          const [startTime, endTime, rowIdx, , , displayValue] = p.value as [
            number,
            number,
            number,
            string,
            string,
            string,
          ];
          const compressor = compressorOrder[rowIdx as number] ?? '';
          const start = formatFacilityDate({
            date: startTime,
            options: { hour: 'numeric', minute: '2-digit', hour12: true },
            withTimeZone: false,
          });
          const end = formatFacilityDate({
            date: endTime,
            options: { hour: 'numeric', minute: '2-digit', hour12: true },
            withTimeZone: false,
          });
          return `<b>${compressor}</b><br/>${displayValue}<br/>${start} — ${end}`;
        },
      },
      series: [
        {
          type: 'custom',
          renderItem,
          data,
          encode: {
            x: [0, 1],
            y: 2,
          },
          clip: true,
          z: 2,
          zlevel: 0,
        },
        {
          type: 'line',
          data: [],
          z: 50,
          zlevel: 3,
          markLine: {
            silent: true,
            symbol: 'none',
            label: { show: false },
            z: 60,
            zlevel: 4,
            lineStyle: { color: '#ffffff', width: 2, type: 'solid' },
            data: [{ xAxis: now }],
          },
        },
      ],
    };
  }, [forecast, now, compressorOrder]);
};
